import DamageNumber from "./DamageNumber";

export default class DamageNumberGroup {
  stored: DamageNumber[];
  spawned: DamageNumber[];
  scene: Phaser.Scene;
  private readonly MAX_CONCURRENT_DAMAGE_NUMBERS = 40; // Safety limit

  constructor(scene: Phaser.Scene) {
    this.stored = new Array();
    this.spawned = new Array();
    this.scene = scene;
  }

  SpawnAt(x: number, y: number, PPower: number) {
    // Safety check: prevent too many concurrent damage numbers
    if (this.spawned.length >= this.MAX_CONCURRENT_DAMAGE_NUMBERS) {
      console.warn(
        `[DamageNumberGroup] Too many concurrent damage numbers (${this.spawned.length}), skipping spawn to prevent performance issues`
      );
      return undefined;
    }

    const number = this.Spawn();

    if (!number) {
      console.error(
        `[DamageNumberGroup] Failed to spawn damage number at (${x}, ${y})`
      );
      return undefined;
    }

    try {
      number.OnRecycle(x, y, PPower);
    } catch (error) {
      console.error(
        `[DamageNumberGroup] Error in OnRecycle for damage number:`,
        error
      );
    }

    return number;
  }

  Spawn() {
    let number = this.stored.pop();
    if (!number) {
      try {
        number = this.Make();
        if (number) {
          number.Init();
        } else {
          console.error(
            `[DamageNumberGroup] Failed to create new damage number`
          );
          return undefined;
        }
      } catch (error) {
        console.error(
          `[DamageNumberGroup] Error creating new damage number:`,
          error
        );
        return undefined;
      }
    } else {
      // Ensure proper reset when reusing from pool
      try {
        number.setVisible(true);
        number.setActive(true); // IMPORTANT: Reactivate the object
        number.setAlpha(1);
        number.setScale(1);
        (number as any)._isReturning = false; // Reset the return flag
      } catch (error) {
        console.error(
          `[DamageNumberGroup] Error resetting damage number:`,
          error
        );
      }
    }

    if (number) {
      this.spawned.push(number);
    }

    return number;
  }

  Return(number: DamageNumber) {
    const index = this.spawned.indexOf(number);
    if (index > -1) {
      this.spawned.splice(index, 1);
    } else {
      console.warn(
        `[DamageNumberGroup] Damage number not found in spawned array - this could indicate a double-return or pool corruption`
      );
    }

    try {
      if (number.tween1) {
        number.tween1.stop();
        number.tween1 = null;
      }
      if (number.tween2) {
        number.tween2.stop();
        number.tween2 = null;
      }

      // Reset visibility and alpha - CRITICAL to hide the damage number
      number.setVisible(false);
      number.setActive(false);
      number.setAlpha(1);
      number.setScale(1);
      // Don't clear text - it will be set in OnRecycle

      this.stored.push(number);
    } catch (error) {
      console.error(
        `[DamageNumberGroup] Error during cleanup and return of damage number:`,
        error
      );
    }
  }

  Make() {
    try {
      const damageNumber = new DamageNumber(this, 0, 0);
      return damageNumber;
    } catch (error) {
      console.error(`[DamageNumberGroup] Error creating DamageNumber:`, error);
      return undefined;
    }
  }

  // Add method to get pool statistics for debugging
  getStats() {
    const stats = {
      stored: this.stored.length,
      spawned: this.spawned.length,
      total: this.stored.length + this.spawned.length,
      spawnedCount: this.spawned.length,
    };
    return stats;
  }
}
